import React, { useState, useRef, useEffect } from 'react';

/**
 * FireworksVoteButton - Vote button with fireworks animation
 *
 * States:
 * - UNVOTED: White outline of a rocket ready to launch (no sparks/beams)
 * - VOTED: Colorful starburst explosion with radiating stars
 * - Animation ONLY when transitioning from UNVOTED -> VOTED
 */

const FireworksVoteButton = ({ isLiked, votes, onLike, disabled }) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [particles, setParticles] = useState([]);
  const rafRef = useRef(null);

  // Brand palette
  const YELLOW = '#FFC324';
  const RED = '#EE3844';
  const BLUE = '#2348fc';
  const PARTICLE_COLORS = [YELLOW, RED, BLUE];

  const generateParticles = () => {
    const n = 20;
    return Array.from({ length: n }, (_, i) => {
      const angle = (i / n) * Math.PI * 2 + (Math.random() - 0.5) * 0.5;
      const v = 3 + Math.random() * 3;
      const color = PARTICLE_COLORS[Math.floor(Math.random() * PARTICLE_COLORS.length)];
      return { 
        id: i, 
        x: 0, 
        y: 0, 
        vx: Math.cos(angle) * v, 
        vy: Math.sin(angle) * v, 
        life: 1, 
        size: 2.5 + Math.random() * 4, 
        color 
      };
    });
  };

  const handleClick = (e) => {
    e.stopPropagation();
    if (disabled || !onLike) return;

    // If animating already, cancel and toggle immediately
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      setIsAnimating(false);
      setParticles([]);
      onLike(e);
      return;
    }

    // Animate only on UNVOTED -> VOTED
    if (!isLiked) {
      setIsAnimating(true);
      setParticles(generateParticles());

      let frame = 0;
      const tick = () => {
        frame += 1;
        setParticles((prev) =>
          prev
            .map((p) => ({
              ...p,
              x: p.x + p.vx,
              y: p.y + p.vy,
              vy: p.vy + 0.25,
              life: p.life - 0.05,
            }))
            .filter((p) => p.life > 0)
        );
        if (frame < 20) {
          rafRef.current = requestAnimationFrame(tick);
        } else {
          rafRef.current = null;
          setIsAnimating(false);
          setParticles([]);
          // ✅ CRITICAL: Call onLike AFTER animation completes
          // Use a fresh event object to ensure proper handling
          onLike({ stopPropagation: () => {} });
        }
      };
      rafRef.current = requestAnimationFrame(tick);
    } else {
      // VOTED -> UNVOTED (no animation, call immediately)
      onLike(e);
    }
  };

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative w-10 h-10">
        {/* Subtle glow while animating */}
        {isAnimating && (
          <div className="absolute inset-0 bg-[#EE3844] rounded-full blur-lg opacity-40 animate-pulse" />
        )}

        {/* Particle layer */}
        <svg 
          className="absolute inset-0 w-full h-full overflow-visible pointer-events-none"
        >
          {particles.map((p) => (
            <circle
              key={p.id}
              cx={20 + p.x}
              cy={20 + p.y}
              r={p.size * p.life}
              fill={p.color}
              opacity={p.life * 0.85}
            />
          ))}
        </svg>

       <button
          className={`w-full h-full relative bg-transparent border-none focus:outline-none z-10 flex items-center justify-center transition-transform ${
            disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer hover:scale-110 active:scale-95'
          }`}
          onClick={handleClick}
          aria-label="Vote in favour"
          type="button"
          disabled={disabled}
        >
          <svg
            viewBox="0 0 64 64"
            className="w-full h-full transition-colors"
            fill="none"
          >
            {isLiked ? (
              /* ================= VOTED: STARBURST EXPLOSION ================= */
              <g strokeLinecap="round" strokeLinejoin="round">
                {/* Center burst star */}
                <path
                  d="M32 28 L33.5 31 L37 31.5 L34.5 34 L35 37.5 L32 35.5 L29 37.5 L29.5 34 L27 31.5 L30.5 31 Z"
                  fill={YELLOW}
                  stroke={YELLOW}
                  strokeWidth="1"
                />
                
                {/* Large outer stars (4 cardinal directions) */}
                <path
                  d="M32 8 L34.5 13 L40 14 L36 18 L37 24 L32 21 L27 24 L28 18 L24 14 L29.5 13 Z"
                  fill={RED}
                  stroke={RED}
                  strokeWidth="1.2"
                />
                <path
                  d="M32 56 L34.5 51 L40 50 L36 46 L37 40 L32 43 L27 40 L28 46 L24 50 L29.5 51 Z"
                  fill={YELLOW}
                  stroke={YELLOW}
                  strokeWidth="1.2"
                />
                <path
                  d="M8 32 L13 34.5 L14 40 L18 36 L24 37 L21 32 L24 27 L18 28 L14 24 L13 29.5 Z"
                  fill={BLUE}
                  stroke={BLUE}
                  strokeWidth="1.2"
                />
                <path
                  d="M56 32 L51 34.5 L50 40 L46 36 L40 37 L43 32 L40 27 L46 28 L50 24 L51 29.5 Z"
                  fill={RED}
                  stroke={RED}
                  strokeWidth="1.2"
                />

                {/* Medium stars (4 diagonal directions) */}
                <path
                  d="M18 18 L19.5 21 L23 21.5 L20.5 24 L21 27.5 L18 25.5 L15 27.5 L15.5 24 L13 21.5 L16.5 21 Z"
                  fill={YELLOW}
                  stroke={YELLOW}
                  strokeWidth="1"
                />
                <path
                  d="M46 18 L47.5 21 L51 21.5 L48.5 24 L49 27.5 L46 25.5 L43 27.5 L43.5 24 L41 21.5 L44.5 21 Z"
                  fill={BLUE}
                  stroke={BLUE}
                  strokeWidth="1"
                />
                <path
                  d="M18 46 L19.5 43 L23 42.5 L20.5 40 L21 36.5 L18 38.5 L15 36.5 L15.5 40 L13 42.5 L16.5 43 Z"
                  fill={RED}
                  stroke={RED}
                  strokeWidth="1"
                />
                <path
                  d="M46 46 L47.5 43 L51 42.5 L48.5 40 L49 36.5 L46 38.5 L43 36.5 L43.5 40 L41 42.5 L44.5 43 Z"
                  fill={YELLOW}
                  stroke={YELLOW}
                  strokeWidth="1"
                />

                {/* Small accent stars scattered around */}
                <path
                  d="M24 12 L25 14 L27 14.5 L25.5 16 L26 18 L24 17 L22 18 L22.5 16 L21 14.5 L23 14 Z"
                  fill={BLUE}
                  opacity="0.9"
                />
                <path
                  d="M40 12 L41 14 L43 14.5 L41.5 16 L42 18 L40 17 L38 18 L38.5 16 L37 14.5 L39 14 Z"
                  fill={YELLOW}
                  opacity="0.9"
                />
                <path
                  d="M12 24 L13 26 L15 26.5 L13.5 28 L14 30 L12 29 L10 30 L10.5 28 L9 26.5 L11 26 Z"
                  fill={RED}
                  opacity="0.9"
                />
                <path
                  d="M52 24 L53 26 L55 26.5 L53.5 28 L54 30 L52 29 L50 30 L50.5 28 L49 26.5 L51 26 Z"
                  fill={YELLOW}
                  opacity="0.9"
                />
                <path
                  d="M12 40 L13 38 L15 37.5 L13.5 36 L14 34 L12 35 L10 34 L10.5 36 L9 37.5 L11 38 Z"
                  fill={BLUE}
                  opacity="0.9"
                />
                <path
                  d="M52 40 L53 38 L55 37.5 L53.5 36 L54 34 L52 35 L50 34 L50.5 36 L49 37.5 L51 38 Z"
                  fill={RED}
                  opacity="0.9"
                />
                <path
                  d="M24 52 L25 50 L27 49.5 L25.5 48 L26 46 L24 47 L22 46 L22.5 48 L21 49.5 L23 50 Z"
                  fill={YELLOW}
                  opacity="0.9"
                />
                <path
                  d="M40 52 L41 50 L43 49.5 L41.5 48 L42 46 L40 47 L38 46 L38.5 48 L37 49.5 L39 50 Z"
                  fill={RED}
                  opacity="0.9"
                />

                {/* Tiny sparkle stars for extra wow */}
                <path d="M20 10 L20.5 11 L21.5 11.2 L20.7 12 L21 13 L20 12.5 L19 13 L19.3 12 L18.5 11.2 L19.5 11 Z" fill={RED} opacity="0.8" />
                <path d="M44 10 L44.5 11 L45.5 11.2 L44.7 12 L45 13 L44 12.5 L43 13 L43.3 12 L42.5 11.2 L43.5 11 Z" fill={BLUE} opacity="0.8" />
                <path d="M10 20 L10.5 21 L11.5 21.2 L10.7 22 L11 23 L10 22.5 L9 23 L9.3 22 L8.5 21.2 L9.5 21 Z" fill={YELLOW} opacity="0.8" />
                <path d="M54 20 L54.5 21 L55.5 21.2 L54.7 22 L55 23 L54 22.5 L53 23 L53.3 22 L52.5 21.2 L53.5 21 Z" fill={BLUE} opacity="0.8" />
                <path d="M10 44 L10.5 43 L11.5 42.8 L10.7 42 L11 41 L10 41.5 L9 41 L9.3 42 L8.5 42.8 L9.5 43 Z" fill={RED} opacity="0.8" />
                <path d="M54 44 L54.5 43 L55.5 42.8 L54.7 42 L55 41 L54 41.5 L53 41 L53.3 42 L52.5 42.8 L53.5 43 Z" fill={YELLOW} opacity="0.8" />
                <path d="M20 54 L20.5 53 L21.5 52.8 L20.7 52 L21 51 L20 51.5 L19 51 L19.3 52 L18.5 52.8 L19.5 53 Z" fill={BLUE} opacity="0.8" />
                <path d="M44 54 L44.5 53 L45.5 52.8 L44.7 52 L45 51 L44 51.5 L43 51 L43.3 52 L42.5 52.8 L43.5 53 Z" fill={YELLOW} opacity="0.8" />
              </g>
            ) : (
              /* ================= UNVOTED: ROCKET READY TO LAUNCH ================= */
              <g stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" opacity="0.9">
                {/* Rocket body */}
                <path d="M 28 45 L 28 28 L 32 22 L 36 28 L 36 45 Z" fill="none" />
                
                {/* Rocket nose cone */}
                <path d="M 28 28 L 32 18 L 36 28" fill="none" />
                
                {/* Rocket fins */}
                <path d="M 28 38 L 24 42 L 28 45" fill="none" />
                <path d="M 36 38 L 40 42 L 36 45" fill="none" />
                
                {/* Rocket window */}
                <circle cx="32" cy="32" r="2.5" fill="none" />
                
                {/* Launch pad base */}
                <line x1="22" y1="48" x2="42" y2="48" strokeWidth="2.5" />
                <line x1="24" y1="48" x2="24" y2="51" strokeWidth="2" />
                <line x1="40" y1="48" x2="40" y2="51" strokeWidth="2" />
              </g>
            )}
          </svg>
        </button>
      </div>

      {/* Vote count */}
      <span className="text-[#FFC324] text-xs font-semibold tabular-nums">
        {votes}
      </span>
    </div>
  );
};

export default FireworksVoteButton;